import { sdk } from '@/lib/sdk'
import type { DownloadFileLimits } from '@/types/downloads'
import { fileProblemText } from './downloadFormatText'
import { checkFile } from './downloadUtils'
import { StorageRefused, UploadCanceled, uploadItemFile, type UploadProgress } from './downloadUpload'

/** The file a one-file space is born with (F-75): its item's title, description and the file itself. */
export interface DownloadSingleDraft {
  title: string
  description: string
  file: File | null
}

export const emptyDownloadSingleDraft = (): DownloadSingleDraft => ({ title: '', description: '', file: null })

/** How far a previous attempt got, so a retry never creates a second item or sends the file twice. */
export interface DownloadSingleProgress {
  itemId?: string
  uploaded?: boolean
}

export interface DownloadSingleErrors {
  title?: string
  file?: string
}

/** Field errors before anything exists: a title and an accepted file. */
export const validateDownloadSingle = (
  draft: DownloadSingleDraft,
  t: (key: string, vars?: Record<string, unknown>) => string,
  locale: string,
  limits?: DownloadFileLimits | null
): DownloadSingleErrors | null => {
  const errors: DownloadSingleErrors = {}
  if (!draft.title.trim()) errors.title = t('downloads:form.errors.title')
  if (!draft.file) errors.file = t('downloads:wizard.fileRequired')
  else {
    const problem = checkFile(draft.file, limits)
    if (problem) errors.file = fileProblemText(t as never, locale, problem, draft.file.name, limits)
  }
  return errors.title || errors.file ? errors : null
}

/**
 * The item as a draft, then its file through the same upload as the item's page (upload-url,
 * direct PUT with progress, confirm). `progress` carries what a refused attempt already did.
 * Throws with the reason; `onStep` reports each step that went through.
 */
export const createDownloadSingle = async (
  spaceId: string,
  draft: DownloadSingleDraft,
  progress: DownloadSingleProgress,
  onStep: (progress: DownloadSingleProgress) => void,
  onUpload: (upload: UploadProgress) => void,
  signal: AbortSignal
): Promise<string> => {
  let itemId = progress.itemId
  const description = draft.description.replace(/<[^>]*>/g, '').trim() ? draft.description : null
  if (!itemId) {
    /* Born a draft: the person publishes it from its page, with the e-mail choice there. */
    const created = await sdk.downloads.createItem({ spaceId, title: draft.title.trim(), description, notifyByEmail: false })
    itemId = created.id
    onStep({ itemId })
  } else {
    await sdk.downloads.updateItem(itemId, { title: draft.title.trim(), description })
  }
  if (!progress.uploaded && draft.file) {
    await uploadItemFile(itemId, draft.file, onUpload, signal)
    onStep({ itemId, uploaded: true })
  }
  return itemId
}

/** Why `createDownloadSingle` stopped, in the person's words. */
export const downloadSingleFailure = (err: unknown, t: (key: string) => string): string | null => {
  if (err instanceof UploadCanceled) return null
  if (err instanceof StorageRefused) return t('downloads:files.errors.storage')
  const message = (err as { response?: { data?: { message?: string } } } | null)?.response?.data?.message
  return message || t('downloads:files.errors.generic')
}
